import {
  createListenerMiddleware, isAnyOf, ListenerEffect, ListenerEffectAPI, PayloadAction,
} from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { RehydrateAction } from 'redux-persist/es/types';

import { AppDispatch, RootState } from 'App/Store';
import { AppStartListening } from 'App/ListenerMiddleware';

import authApi from 'modules/auth/service';
import kioskApi from 'modules/kiosk/service';
import socketSlice from 'modules/sockets/reducer';
import selfServicesApi from 'modules/selfServices/service';

import { locationChange, push } from 'modules/router/actions';
import { addNotification } from 'modules/notifications/actions';
import { closePayingModal, setPaymentStatus } from 'modules/onlinePayment/actions';
import {
  setDoorError, setDoorStatus, setIsKioskOffline, setIsKioskReady,
} from 'modules/kiosk/actions';

import { getSearch, isHomePage, isLogin, isReopenKioskPage } from 'modules/router/selectors';
import { getSelectedSelfService } from 'modules/selfServices/selectors';
import { isLocalKiosk, isPublicDevice } from 'modules/dealers/selectors';
import { getStepsConfig } from 'modules/selfServices/stepsConfigSelectors';
import { getStepFromPath, toUrlSlug } from 'utils/stepUrlUtils';
import { getAdminToken, getSelectedSelfServiceId, getToken } from 'modules/auth/selectors';
import { isPayingModalOpen as isPayingModalOpenSelector } from 'modules/onlinePayment/selectors';

import {
  IncomingMessage,
  KioskPaymentUpdate,
  KioskOperationUpdate,
  QrCodeLoginPayload,
  QrCodeTestFactoryPayload,
  SocketResponseAction,
  SocketResponseUpdate,
  isSocketResponseUpdate,
} from 'modules/sockets/types/SocketResponse';
import { DoorsStatus } from 'modules/kiosk/types/KeysSafe';
import PaymentStatus from 'modules/onlinePayment/types/PaymentStatus';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';
import { NotificationType } from 'modules/notifications/types/Notification';
import { FinalInvoiceStatus, SelfServiceType } from 'modules/selfServices/types/SelfService';

import { getSelfServiceSteps } from 'components/SelfServices/Steps';

import * as actions from './actions';
import { isInstructionModalDisplayed } from './selectors';
import { POLL_INTERVAL } from './constants';
import { getUrlParam } from 'utils/urlUtils';
import socketApi from 'modules/sockets/service';
import { QrCodeLoginMethod } from 'modules/auth/types/LoginRequest';

let socket: WebSocket;
let kioskSocket: WebSocket;
let socketConnection: Promise<void> | null = null;

const WSOCKET_URL = process.env.WSOCKET_URL;

const listenerMiddleware = createListenerMiddleware();

const startAppListening = listenerMiddleware.startListening as AppStartListening;

type KeepAliveEffect = ListenerEffect<ReturnType<typeof actions.initialized>, RootState, AppDispatch>;
type KeepAliveKioskEffect = ListenerEffect<ReturnType<typeof actions.kioskSocketInitialized>, RootState, AppDispatch>;

export const isConnected = (wsocket: WebSocket): boolean => wsocket instanceof WebSocket
  && (wsocket.readyState === WebSocket.OPEN || wsocket.readyState === WebSocket.CONNECTING);

const createSocket = (path: string) => new Promise<WebSocket>((resolve, reject) => {
  const wsocket = new WebSocket(path);
  wsocket.addEventListener('open', () => resolve(wsocket), { once: true });
  wsocket.addEventListener('error', reject, { once: true });
});

const onReceiveMessage = (dispatch: AppDispatch) => async ({ data: stringifiedData }: MessageEvent | { data: string }) => {
  const socketResponse = JSON.parse(stringifiedData);
  if (isSocketResponseUpdate(socketResponse)) {
    if (socketResponse.action === SocketResponseAction.INTERVENTION_UPDATE) {
      dispatch(actions.selfServiceUpdated(socketResponse));
    } else if (socketResponse.action === SocketResponseAction.MESSAGE_SENT) {
      dispatch(actions.newIncomingMessage(socketResponse as unknown as IncomingMessage));
    } else if (socketResponse.action === SocketResponseAction.MESSAGE_REACTION_REACT ||
               socketResponse.action === SocketResponseAction.MESSAGE_REACTION_UNREACT) {
      dispatch(actions.messageUpdateReaction(socketResponse as unknown as IncomingMessage));
    } else if (socketResponse.action === SocketResponseAction.KIOSK_PAYMENT_UPDATE) {
      dispatch(actions.kioskPaymentUpdated(socketResponse as unknown as KioskPaymentUpdate));
    } else if (socketResponse.action === SocketResponseAction.KIOSK_OPERATION_UPDATE) {
      dispatch(actions.kioskOperationUpdated(socketResponse as unknown as KioskOperationUpdate));
    } else if (socketResponse.action === SocketResponseAction.KIOSK_ONLINE
      || socketResponse.action === SocketResponseAction.KIOSK_ALARM_EXITED) {
      dispatch(setIsKioskOffline(false));
    } else if (socketResponse.action === SocketResponseAction.KIOSK_OFFLINE
      || socketResponse.action === SocketResponseAction.KIOSK_ALARM_ENTERED) {
      dispatch(setIsKioskOffline(true));
    }
  }
};

export const handleOnError = (dispatch: AppDispatch) => (event: Event) => {
  dispatch(actions.socketError(event));
};

const closeSocket = () => {
  if (isConnected(socket)) {
    socket.close();
  }
};

// Socket for KIOSK_OPERATION_UPDATE — initialized from home page with kiosk token
export const startKioskTokenAuthenticatedSocket = async (dispatch: AppDispatch) => {
  const kioskToken = getUrlParam('OSToken') ?? getUrlParam('token');

  if (kioskToken && !isConnected(kioskSocket)) {
    try {
      kioskSocket = await createSocket(`${WSOCKET_URL}?token=${kioskToken}`);

      kioskSocket.addEventListener('error', handleOnError(dispatch));
      kioskSocket.addEventListener('message', onReceiveMessage(dispatch));
      kioskSocket.addEventListener('close', () => startKioskTokenAuthenticatedSocket(dispatch));

      dispatch(actions.kioskSocketInitialized());
    } catch {
      dispatch(actions.creationFailure());
      console.error('Impossible to connect to kiosk socket');
    }
  }
};

export const startSocket = async (getState: () => RootState, dispatch: AppDispatch) => {
  const userToken = getToken(getState());
  const adminToken = getAdminToken(getState());
  const isHomePageLocation = isHomePage(getState());

  const token = userToken ?? adminToken;

  if (isHomePageLocation || !token || isConnected(socket) || socketConnection) {
    return;
  }

  socketConnection = (async () => {
    try {
      const { data } = await dispatch(socketApi.endpoints.getAuthToken.initiate(token));
      socket = await createSocket(`${WSOCKET_URL}?token=${data.token}`);

      socket.addEventListener('error', handleOnError(dispatch));
      socket.addEventListener('message', onReceiveMessage(dispatch));
      socket.addEventListener('close', () => startSocket(getState, dispatch));

      dispatch(actions.initialized());
    } catch {
      dispatch(actions.creationFailure());
      console.error('Impossible to connect to socket with error');
    }
  })();

  try {
    await socketConnection;
  } finally {
    socketConnection = null;
  }
};

export const keepAlive: KeepAliveEffect = async (_, api) => {
  // Cancel any other running `keepAlive` when the socket disconnected AND reconnected faster than `POLL_INTERVAL`
  api.cancelActiveListeners();

  while (isConnected(socket)) {
    api.dispatch(actions.sendMessage('ping'));
    await api.delay(POLL_INTERVAL);
  }
};

export const keepAliveKioskSocket: KeepAliveKioskEffect = async (_, api) => {
  api.cancelActiveListeners();

  while (isConnected(kioskSocket)) {
    kioskSocket.send('ping');
    await api.delay(POLL_INTERVAL);
  }
};

export const handleSelfServiceUpdate = async (
  { payload }: PayloadAction<SocketResponseUpdate>,
  { dispatch, getState, take, condition }: ListenerEffectAPI<RootState, AppDispatch>
) => {
  const currentStep = getStepFromPath(window.location.pathname);
  const hasSelfService = getSelectedSelfService(getState())
    ? true
    : await condition(selfServicesApi.endpoints.getSelfServiceById.matchFulfilled, 10000);

  // If we are on the final instructions step, we don't want to do anything
  if (!hasSelfService || currentStep === SelfServiceSteps.FINAL_INSTRUCTIONS) {
    return;
  }

  const state = getState();
  const { updatedFinalInvoice, checkOutCompleted } = payload.data;
  const { type, id: selfServiceId } = getSelectedSelfService(getState());
  const shouldOpenLogoutModal = type === SelfServiceType.CHECK_IN && (updatedFinalInvoice || checkOutCompleted);

  if (shouldOpenLogoutModal) {
    dispatch(socketSlice.actions.openLogoutModal());
  } else if (currentStep === SelfServiceSteps.VEHICLE_CHECK_QR_CODE) {
    // We redirect only on public devices
    const isPublic = isPublicDevice(state);
    const { vehicleCheckStarted } = payload.data;

    if (isPublic && vehicleCheckStarted) {
      dispatch(push(`/checkin/${toUrlSlug(SelfServiceSteps.FINAL_INSTRUCTIONS)}${window.location.search}`));
    }
  } else if (currentStep === SelfServiceSteps.FINAL_INVOICE) {
    const { paymentInProgress, updatedPayment } = payload.data;

    // Only payment events carry a payment status: the back-end never pushes a payment failure,
    // so any other update (medias, final invoice documents, ...) must not touch it
    if (paymentInProgress) {
      dispatch(setPaymentStatus(PaymentStatus.PENDING));
    } else if (updatedPayment) {
      dispatch(setPaymentStatus(PaymentStatus.SUCCESS));
    }

    if (!paymentInProgress) {
      if (isPayingModalOpenSelector(state)) {
        await take(closePayingModal.match);
      }
      // We refetch the self-service to get the updated final invoice
      dispatch(selfServicesApi.endpoints.getSelfServiceById.initiate({ id: selfServiceId }, { forceRefetch: true }));
    }
  } else {
    try {
      const data = await dispatch(
        selfServicesApi.endpoints.getSelfServiceById.initiate({ id: selfServiceId }, { forceRefetch: true })
      ).unwrap();

      // Order of steps matters!
      const selfServiceSteps = getSelfServiceSteps(data, getStepsConfig(state));

      const { key: stepKey } =
        selfServiceSteps.find(({ data: stepData }) => stepData.socketUpdateFields?.some(field => payload.data[field])) ??
      {};

      // We redirect to the first step between the current step and the updated step
      const stepToRedirectTo =
        selfServiceSteps.find(({ key }) => key === stepKey || key === currentStep)?.key ?? selfServiceSteps[0].key;
      dispatch(socketSlice.actions.displayModal(stepToRedirectTo));
    } catch {
      // Redirect to main page if self-service cannot be fetched
      dispatch(push(`/${getSearch(state)}`));
      return;
    }
  }
};


export const handleKioskPaymentUpdate = async (
  { payload }: PayloadAction<KioskPaymentUpdate>,
  { dispatch, getState }: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const state = getState();
  const isKiosk = isLocalKiosk(state);
  const selfServiceId = getSelectedSelfServiceId(state);
  const isInstructionOpen = isInstructionModalDisplayed(state);

  const { isSuccess: isSelfServiceSuccess, data: selfService } = await dispatch(
    selfServicesApi.endpoints.getSelfServiceById.initiate({ id: selfServiceId }, { forceRefetch: true }),
  );

  if (isKiosk && isSelfServiceSuccess) {
    const { data: websocketData } = payload;

    // If all flags are set to false, it means that the payment is successful
    let [errorType] = Object.entries(websocketData).find(([, value]) => value === true) ?? [];
    // There is no distinction from client side between those 2 errors
    if (errorType === 'providerError' || errorType === 'hostUnavailable') {
      errorType = 'unknownError';
    }

    const isError = isInstructionOpen
      && selfService.finalInvoice.status !== FinalInvoiceStatus.PAID
      && (errorType || selfService.finalInvoice.aborted || selfService.finalInvoice.failed);

    // Wait a bit before closing the modal to let the user see the success icon or the error message
    setTimeout(
      () => dispatch(socketSlice.actions.closeInstructionModal()),
      2000,
    );

    if (isError) {
      dispatch(
        addNotification({
          title: {
            id: `page.finalInvoice.payment.error.${errorType}.title`,
            defaultMessage: 'An error occur during the payment process',
          },
          description: {
            id: `page.finalInvoice.payment.error.${errorType}.description`,
            defaultMessage: 'Please try again later or contact us.',
          },
          type: NotificationType.ERROR,
        }),
      );
    }
  }
};

export const handleQrCodeLogin = async (
  { payload }: PayloadAction<QrCodeLoginPayload>,
  { dispatch }: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  console.log('[sockets] handleQrCodeLogin — dispatching sendQrCode', { type: payload.type });
  dispatch(authApi.endpoints.sendQrCode.initiate({ qrCode: payload.code, type: payload.type, loginMethod: QrCodeLoginMethod.QR_CODE }));
};

export const handleQrCodeTestFactory = async (
  { payload }: PayloadAction<QrCodeTestFactoryPayload>,
  { dispatch }: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const { messageId } = payload;
  dispatch(addNotification({
    title: {
      id: `qrCode.message.${messageId}`,
      defaultMessage: messageId,
    },
    type: NotificationType.SUCCESS,
  }));
};

export const handleKioskOperationUpdate = async (
  { payload }: PayloadAction<KioskOperationUpdate>,
  { dispatch, getState }: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const [key] = Object.entries(payload.data).find(([, value]) => value) ?? [];
  const isQrCodePage = isHomePage(getState()) || isLogin(getState()) || isReopenKioskPage(getState());

  console.log('[REOPEN][sockets] handleKioskOperationUpdate', { key, isQrCodePage, pathname: getState().router?.location?.pathname });
  if (key === 'qrCodeScanned' && isQrCodePage) {
    const { additionalData } = payload.data;
    if ('payload' in additionalData) {
      const { payload, type } = additionalData;
      const hasData = payload && Object.keys(payload).length;
      if (hasData) {
        console.log('[REOPEN][sockets] qrCodeScanned — dispatching QR_CODE_ACTION_MAP', { type });
        dispatch(actions.QR_CODE_ACTION_MAP[type](payload));
      }
    } else {
      // TODO remove once the qr code is properly migrated
      const { code, type } = additionalData;
      if (code && type) {
        console.log('[REOPEN][sockets] qrCodeScanned (legacy) — dispatching sendQrCode', { type });
        dispatch(authApi.endpoints.sendQrCode.initiate({ qrCode: code, type: type, loginMethod: QrCodeLoginMethod.QR_CODE }));
      }
    }
  } else if (key === 'qrCodeScanned' && !isQrCodePage) {
    console.log('[REOPEN][sockets] qrCodeScanned IGNORED — not on a QR code page', { pathname: getState().router?.location?.pathname });
  } else if (key === 'doorPrepared' || key === 'doorPrepareFailed') {
    // The preparation will be re-trigger during the opening process in case of failure.
    dispatch(setIsKioskReady(true));
  } else if (key === 'doorOpened' || key === 'doorClosed') {
    const status = key === 'doorOpened' ? DoorsStatus.OPENED : DoorsStatus.CLOSED;
    dispatch(setDoorStatus(status));
  } else if (key === 'doorOpenFailed' || key === 'doorCloseFailed' || key === 'doorOpenCancelled') {
    dispatch(setDoorError(true));
    const status = key === 'doorCloseFailed' ? DoorsStatus.OPENED : DoorsStatus.PENDING;
    const origin = key === 'doorCloseFailed' ? DoorsStatus.CLOSED : DoorsStatus.OPENED;
    dispatch(setDoorStatus(status, { origin }));
  }
};

function isRehydrateAction(action: PayloadAction<RehydrateAction>): action is PayloadAction<RehydrateAction> {
  return action.type === REHYDRATE;
}

// Create and initialized socket
startAppListening({
  matcher: isAnyOf(
    isRehydrateAction,
    selfServicesApi.endpoints.getSelfServiceById.matchFulfilled,
    authApi.endpoints.loginAdmin.matchFulfilled,
  ),
  effect: async (_, { dispatch, getState }) => startSocket(getState, dispatch),
});


// Create and initialize kiosk socket — active from home page, uses kiosk token from URL
startAppListening({
  matcher: isRehydrateAction,
  effect: async (_, { dispatch }) => startKioskTokenAuthenticatedSocket(dispatch),
});

// After kiosk socket initialized
startAppListening({
  actionCreator: actions.kioskSocketInitialized,
  effect: keepAliveKioskSocket,
});

// When a message is sent on the socket
startAppListening({
  actionCreator: actions.sendMessage,
  effect: ({ payload }) => {
    if (isConnected(socket)) {
      socket.send(payload);
    }
  },
});

// After initialized the socket
startAppListening({
  actionCreator: actions.initialized,
  effect: keepAlive,
});

// Handle selfServiceUpdated action
startAppListening({
  actionCreator: actions.selfServiceUpdated,
  effect: handleSelfServiceUpdate,
});

// Handle kioskPaymentUpdated action
startAppListening({
  actionCreator: actions.kioskPaymentUpdated,
  effect: handleKioskPaymentUpdate,
});

// Handle kioskOperationUpdated action
startAppListening({
  actionCreator: actions.kioskOperationUpdated,
  effect: handleKioskOperationUpdate,
});


// Qr code action listener //
startAppListening({
  actionCreator: actions.qrCodeLogin,
  effect: handleQrCodeLogin,
});

startAppListening({
  actionCreator: actions.qrCodeTestFactory,
  effect: handleQrCodeTestFactory,
});
// // //

// Close socket when going back to the home page
startAppListening({
  matcher: locationChange.match,
  effect: (_, { getState }) => {
    if (isHomePage(getState())) {
      closeSocket();
    }
  },
});

startAppListening({
  matcher: kioskApi.endpoints.getLatestMessage.matchFulfilled,
  effect: ({ payload }, { dispatch }) => onReceiveMessage(dispatch)({ data: payload.message }),
});

export default listenerMiddleware;
